import React, { useMemo, useState } from 'react';
import { Alert, Button, Modal, Table, Upload, message } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import type { EmployerCreateRequestDTO } from '@/shared/api/model';
import { useCreateEmployer } from '@/shared/api/client/employer-controller';

type ImportRow = EmployerCreateRequestDTO & { __row: number };

type ImportError = {
    row: number;
    field?: string;
    message: string;
};

interface EmployerImportModalProps {
    open: boolean;
    onClose: () => void;
    onImported: () => void;
}

const parseCsv = (csvText: string): { headers: string[]; rows: string[][] } => {
    const rows: string[][] = [];
    let currentField = '';
    let currentRow: string[] = [];
    let inQuotes = false;

    const pushField = () => {
        currentRow.push(currentField);
        currentField = '';
    };

    const pushRow = () => {
        if (currentRow.length === 1 && currentRow[0] === '') {
            currentRow = [];
            return;
        }
        rows.push(currentRow);
        currentRow = [];
    };

    for (let i = 0; i < csvText.length; i += 1) {
        const char = csvText[i];
        const nextChar = csvText[i + 1];

        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                currentField += '"';
                i += 1;
                continue;
            }
            inQuotes = !inQuotes;
            continue;
        }

        if (!inQuotes && char === ',') {
            pushField();
            continue;
        }

        if (!inQuotes && (char === '\n' || char === '\r')) {
            if (char === '\r' && nextChar === '\n') {
                i += 1;
            }
            pushField();
            pushRow();
            continue;
        }

        currentField += char;
    }

    pushField();
    pushRow();

    const headers = (rows.shift() || []).map((h) => h.trim());
    return { headers, rows };
};

const normalizeHeader = (h: string) => h.trim().toLowerCase().replace(/\s+/g, '');

const downloadCsv = (filename: string, csv: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

export const EmployerImportModal: React.FC<EmployerImportModalProps> = ({ open, onClose, onImported }) => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [parsedRows, setParsedRows] = useState<ImportRow[]>([]);
    const [errors, setErrors] = useState<ImportError[]>([]);

    const createEmployer = useCreateEmployer();

    const templateCsv = useMemo(() => {
        return [
            ['name', 'voteNumber', 'address', 'poBox', 'phone', 'email'].join(','),
            ['Example Employer', 'VOTE-123', 'Dar es Salaam', '123', '255700000000', 'employer@example.com'].join(',')
        ].join('\n');
    }, []);

    const handleParse = async (file: File) => {
        const text = await file.text();
        const { headers, rows } = parseCsv(text);

        const headerMap = new Map<string, number>();
        headers.forEach((h, idx) => headerMap.set(normalizeHeader(h), idx));

        const get = (row: string[], key: string) => {
            const idx = headerMap.get(normalizeHeader(key));
            if (idx === undefined) return undefined;
            return (row[idx] || '').trim();
        };

        const nextRows: ImportRow[] = [];
        const nextErrors: ImportError[] = [];
        const voteNumberSeen = new Set<string>();

        rows.forEach((r, index) => {
            const rowNumber = index + 2;
            const name = get(r, 'name');
            const voteNumber = get(r, 'voteNumber');

            if (!name) {
                nextErrors.push({ row: rowNumber, field: 'name', message: 'Name is required' });
            }

            if (voteNumber) {
                const key = voteNumber.toLowerCase();
                if (voteNumberSeen.has(key)) {
                    nextErrors.push({ row: rowNumber, field: 'voteNumber', message: 'Duplicate voteNumber in file' });
                }
                voteNumberSeen.add(key);
            }

            nextRows.push({
                __row: rowNumber,
                name: name || '',
                voteNumber: voteNumber || undefined,
                address: get(r, 'address') || undefined,
                poBox: get(r, 'poBox') || undefined,
                phone: get(r, 'phone') || undefined,
                email: get(r, 'email') || undefined,
            });
        });

        setParsedRows(nextRows);
        setErrors(nextErrors);

        if (!headers.length || !headerMap.has('name')) {
            message.warning('CSV parsed, but missing expected headers. Required: name');
        }
    };

    const handleStartImport = async () => {
        if (errors.length > 0) {
            message.error('Fix validation errors before importing');
            return;
        }

        if (parsedRows.length === 0) {
            message.error('No rows to import');
            return;
        }

        const importErrors: ImportError[] = [];
        let successCount = 0;

        for (const row of parsedRows) {
            const payload: EmployerCreateRequestDTO = {
                name: (row.name || '').trim(),
                voteNumber: row.voteNumber ? row.voteNumber.trim() : undefined,
                address: row.address ? row.address.trim() : undefined,
                poBox: row.poBox ? row.poBox.trim() : undefined,
                phone: row.phone ? row.phone.trim() : undefined,
                email: row.email ? row.email.trim() : undefined,
            };

            try {
                await createEmployer.mutateAsync({ data: payload });
                successCount += 1;
            } catch (e: any) {
                const apiMessage = e?.response?.data?.message || 'Failed to create employer';
                importErrors.push({ row: row.__row, message: apiMessage });
            }
        }

        setErrors(importErrors);

        if (importErrors.length === 0) {
            message.success(`Imported ${successCount} employers`);
            onImported();
        } else {
            message.warning(`Imported ${successCount} employers, ${importErrors.length} failed`);
        }
    };

    const handleClose = () => {
        setFileList([]);
        setParsedRows([]);
        setErrors([]);
        onClose();
    };

    const errorColumns = [
        { title: 'Row', dataIndex: 'row', key: 'row', width: 90 },
        { title: 'Field', dataIndex: 'field', key: 'field', width: 150 },
        { title: 'Message', dataIndex: 'message', key: 'message' },
    ];

    return (
        <Modal
            open={open}
            title="Import Employers (CSV)"
            onCancel={handleClose}
            footer={null}
            width={900}
            destroyOnClose
        >
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button onClick={() => downloadCsv('employers-template.csv', templateCsv)}>
                    Download Template
                </Button>
            </div>

            <Upload.Dragger
                accept=".csv"
                multiple={false}
                fileList={fileList}
                beforeUpload={async (file) => {
                    setFileList([file as any]);
                    await handleParse(file);
                    return false;
                }}
                onRemove={() => {
                    setFileList([]);
                    setParsedRows([]);
                    setErrors([]);
                }}
            >
                <p style={{ margin: 0 }}>Click or drag CSV file to this area to upload</p>
            </Upload.Dragger>

            <div style={{ marginTop: 16 }}>
                {errors.length > 0 ? (
                    <Alert
                        type="error"
                        message={`Found ${errors.length} issue(s). Fix them and retry.`}
                        showIcon
                    />
                ) : (
                    <Alert
                        type="info"
                        message={parsedRows.length > 0 ? `Ready to import ${parsedRows.length} row(s).` : 'Upload a CSV to preview import.'}
                        showIcon
                    />
                )}
            </div>

            {errors.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <Table
                        rowKey={(r) => `${r.row}-${r.field || ''}-${r.message}`}
                        columns={errorColumns}
                        dataSource={errors}
                        pagination={{ pageSize: 8 }}
                        size="small"
                    />
                </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <Button onClick={handleClose} disabled={createEmployer.isPending}>
                    Close
                </Button>
                <Button
                    type="primary"
                    onClick={handleStartImport}
                    loading={createEmployer.isPending}
                    disabled={fileList.length === 0}
                >
                    Start Import
                </Button>
            </div>
        </Modal>
    );
};
